// src/pages/Client/Profile.jsx
import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  Spinner,
} from "react-bootstrap";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "react-hot-toast";
import axios from "../../api/axios";

const Profile = () => {
  const { user, login } = useAuth();
  const [formData, setFormData] = useState({
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
    phone: user?.phone || "",
    email: user?.email || "",
    address: user?.address || "",
  });
  const [passwords, setPasswords] = useState({
    current: "",
    new: "",
    confirm: "",
  });
  const [updating, setUpdating] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [error, setError] = useState("");

  const handleProfileChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError("");
  };

  const handlePasswordChange = (e) => {
    setPasswords({ ...passwords, [e.target.name]: e.target.value });
    setError("");
  };

  const updateProfile = async (e) => {
    e.preventDefault();
    setUpdating(true);
    setError("");

    // Validation basique
    if (!formData.email || !formData.email.includes("@")) {
      setError("Veuillez entrer une adresse email valide");
      toast.error("Email invalide");
      setUpdating(false);
      return;
    }

    if (!formData.first_name?.trim() || !formData.last_name?.trim()) {
      setError("Le prénom et le nom sont obligatoires");
      toast.error("Prénom et nom obligatoires");
      setUpdating(false);
      return;
    }

    try {
      const response = await axios.put("/auth/profile/", {
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone,
        email: formData.email,
        address: formData.address,
      });

      // Mettre à jour l'utilisateur dans le contexte et le localStorage
      const updatedUser = { ...user, ...response.data };
      localStorage.setItem("user", JSON.stringify(updatedUser));

      // Mettre à jour le contexte d'authentification
      if (login) {
        login(updatedUser);
      }

      toast.success("✅ Profil mis à jour avec succès !");
    } catch (err) {
      console.error("Erreur mise à jour:", err);

      // Gestion des erreurs spécifiques
      if (err.response?.data?.email) {
        const emailError = Array.isArray(err.response.data.email)
          ? err.response.data.email[0]
          : err.response.data.email;
        setError(emailError);
        toast.error(emailError);
      } else if (err.response?.data?.error) {
        setError(err.response.data.error);
        toast.error(err.response.data.error);
      } else if (err.response?.data?.detail) {
        setError(err.response.data.detail);
        toast.error(err.response.data.detail);
      } else {
        setError("Erreur lors de la mise à jour du profil");
        toast.error("Erreur lors de la mise à jour");
      }
    } finally {
      setUpdating(false);
    }
  };

  const updatePassword = async (e) => {
    e.preventDefault();

    if (passwords.new !== passwords.confirm) {
      setError("Les nouveaux mots de passe ne correspondent pas");
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }

    if (passwords.new.length < 6) {
      setError("Le mot de passe doit contenir au moins 6 caractères");
      toast.error("Mot de passe trop court");
      return;
    }

    setChangingPassword(true);
    setError("");

    try {
      await axios.post("/auth/change-password/", {
        old_password: passwords.current,
        new_password: passwords.new,
      });

      toast.success("✅ Mot de passe changé avec succès !");
      setPasswords({ current: "", new: "", confirm: "" });
    } catch (err) {
      console.error("Erreur changement mot de passe:", err);
      setError(
        err.response?.data?.old_password?.[0] || "Erreur lors du changement",
      );
      toast.error(
        err.response?.data?.old_password?.[0] || "Erreur lors du changement",
      );
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <Container className="py-5">
      <Row>
        <Col lg={4} className="mb-4 mb-lg-0">
          <Card className="border-0 shadow-lg rounded-4 text-center">
            <Card.Body className="p-4">
              <div className="display-1 mb-3">👤</div>
              <h3 className="fw-bold">
                {user?.first_name} {user?.last_name}
              </h3>
              <p className="text-muted">{user?.email}</p>
              <div className="mt-3">
                <span className="badge bg-danger px-3 py-2">
                  {user?.role === "client" ? "Client" : user?.role}
                </span>
              </div>
              <hr className="my-4" />
              <div className="text-start">
                <small className="text-muted">Membre depuis</small>
                <p className="mb-0">
                  {new Date(user?.date_joined).toLocaleDateString("fr-FR")}
                </p>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={8}>
          {error && (
            <Alert
              variant="danger"
              className="mb-4"
              dismissible
              onClose={() => setError("")}
            >
              {error}
            </Alert>
          )}

          <Card className="border-0 shadow-lg rounded-4 mb-4">
            <Card.Body className="p-4 p-lg-5">
              <h2 className="fw-bold mb-4" style={{ color: "#C62828" }}>
                📋 Informations personnelles
              </h2>

              <Form onSubmit={updateProfile}>
                <Row>
                  <Col md={6} className="mb-3">
                    <Form.Label>
                      Prénom <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleProfileChange}
                      placeholder="Votre prénom"
                      required
                    />
                  </Col>
                  <Col md={6} className="mb-3">
                    <Form.Label>
                      Nom <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleProfileChange}
                      placeholder="Votre nom"
                      required
                    />
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>
                    Email <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleProfileChange}
                    placeholder="votre@email.com"
                    required
                  />
                  <Form.Text className="text-muted">
                    ⚠️ Si vous changez d'email, vous devrez utiliser le nouvel
                    email pour vous connecter.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Téléphone</Form.Label>
                  <Form.Control
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleProfileChange}
                    placeholder="+123456789"
                  />
                  <Form.Text className="text-muted">
                    Format international recommandé : 223XXXXXXXXX sans le +
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label>Adresse</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="address"
                    value={formData.address}
                    onChange={handleProfileChange}
                    placeholder="Votre adresse complète (rue, ville, code postal, pays)"
                  />
                </Form.Group>

                <Button
                  type="submit"
                  disabled={updating}
                  style={{ backgroundColor: "#C62828", borderColor: "#C62828" }}
                  className="px-4"
                >
                  {updating ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Mise à jour...
                    </>
                  ) : (
                    "💾 Enregistrer les modifications"
                  )}
                </Button>
              </Form>
            </Card.Body>
          </Card>

          <Card className="border-0 shadow-lg rounded-4">
            <Card.Body className="p-4 p-lg-5">
              <h2 className="fw-bold mb-4" style={{ color: "#C62828" }}>
                🔒 Changer le mot de passe
              </h2>

              <Form onSubmit={updatePassword}>
                <Form.Group className="mb-3">
                  <Form.Label>Mot de passe actuel</Form.Label>
                  <Form.Control
                    type="password"
                    name="current"
                    value={passwords.current}
                    onChange={handlePasswordChange}
                    required
                    placeholder="Entrez votre mot de passe actuel"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Nouveau mot de passe</Form.Label>
                  <Form.Control
                    type="password"
                    name="new"
                    value={passwords.new}
                    onChange={handlePasswordChange}
                    required
                    placeholder="Minimum 6 caractères"
                    minLength={6}
                  />
                  <Form.Text className="text-muted">
                    Le mot de passe doit contenir au moins 6 caractères
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label>Confirmer le nouveau mot de passe</Form.Label>
                  <Form.Control
                    type="password"
                    name="confirm"
                    value={passwords.confirm}
                    onChange={handlePasswordChange}
                    required
                    placeholder="Retapez votre nouveau mot de passe"
                    minLength={6}
                  />
                </Form.Group>

                <Button
                  type="submit"
                  disabled={changingPassword}
                  variant="outline-danger"
                  className="px-4"
                >
                  {changingPassword ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Changement...
                    </>
                  ) : (
                    "🔑 Changer le mot de passe"
                  )}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Profile;
